import { getSensorType } from '../../../services/localizationService';

const DEFAULT_LAG_TIME = 5000;

/**
 * Given sensor data and a rule, find all data points that violate the rule.
 * Returns raw (unconsolidated) events.
 */
function findViolations(sensorData, rule) {
  const threshold = parseFloat(rule.value);
  const label = `Event: ${rule.name}`;
  const events = [];

  for (const dp of sensorData) {
    const value = parseFloat(dp.value);
    const violated =
      (rule.operator === 'greater' && value > threshold) ||
      (rule.operator === 'lesser' && value < threshold);

    if (violated) {
      events.push({ id: rule.id, label, startTime: dp.time, endTime: dp.time, value });
    }
  }
  return events;
}

/**
 * Merge events that are within lagTime ms of each other.
 *
 * Merging is by time alone and keeps the first event's identity, so the caller
 * MUST pass the violations of a single rule. Handing it a mixed set silently
 * relabels one rule's events with another's and drops the rest.
 */
function consolidateEvents(events, lagTime) {
  if (events.length === 0) return [];
  const sorted = [...events].sort((a, b) => a.startTime - b.startTime);
  const result = [];
  let current = { ...sorted[0] };

  for (let i = 1; i < sorted.length; i += 1) {
    const next = sorted[i];
    if (current.endTime + lagTime >= next.startTime) {
      // max, not assignment: sorting is by start, so a shorter event can sit
      // wholly inside the one being extended.
      current.endTime = Math.max(current.endTime, next.endTime);
    } else {
      result.push(current);
      current = { ...next };
    }
  }
  result.push(current);
  return result;
}

/**
 * Compute rule-violation events for a single recording.
 *
 * @param {object} recording - DataCapture with .data map
 * @param {object[]} activeRules - array of rule objects
 * @param {number} lagTime - consolidation window in ms
 * @returns {object[]} consolidated events
 */
export function computeEventsForRecording(recording, activeRules, lagTime = DEFAULT_LAG_TIME) {
  if (!recording?.data || activeRules.length === 0) return [];

  const dataKeys = Object.keys(recording.data);
  const events = [];

  for (const rule of activeRules) {
    const ruleSensorType = getSensorType(rule.sensorType);
    const matchingKeys = dataKeys.filter(
      (key) => getSensorType(key.split(':').pop()) === ruleSensorType
    );

    const violations = [];
    for (const key of matchingKeys) {
      violations.push(...findViolations(recording.data[key], rule));
    }

    // Consolidate per rule. Pooling every rule's violations first and merging
    // the lot by time made two rules violated within the lag window come out as
    // one event carrying the first rule's label, with the second rule's
    // violation gone from the timeline, the highlight reel and every widget.
    events.push(...consolidateEvents(violations, lagTime));
  }

  return events.sort((a, b) => a.startTime - b.startTime);
}

/**
 * Compute events across all recordings. Returns a map of recordingId -> events[].
 */
export function computeAllEvents(recordings, activeRules, lagTime = DEFAULT_LAG_TIME) {
  const eventsMap = {};
  for (const rec of recordings) {
    eventsMap[rec.id] = computeEventsForRecording(rec, activeRules, lagTime);
  }
  return eventsMap;
}

/**
 * Merge global (workspace-scoped) and tab-scoped active rules into a single array.
 * Global rules are always active; tab rules are additive.
 * Deduplicates by rule ID.
 *
 * @param {string[]} globalActiveRuleIds - IDs of globally active rules
 * @param {string[]} tabActiveRuleIds - IDs of tab-active rules
 * @param {object[]} allRules - all available rule objects (API + workspace-local + tab-local)
 * @returns {object[]} combined array of active rule objects
 */
export function mergeActiveRules(globalActiveRuleIds = [], tabActiveRuleIds = [], allRules = []) {
  const combinedIds = new Set([...globalActiveRuleIds, ...tabActiveRuleIds]);
  return allRules.filter((r) => combinedIds.has(r.id));
}
